/**
 * ContentThumbnail Component
 *
 * Displays content thumbnail image with error handling.
 * Pure thumbnail display without overlays or controls.
 */

import React, { useEffect, useState } from 'react';
import { CachedImage } from '@/presentation/components/common/CachedImage';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { qaAttr, type QaAttrOptions } from '@/presentation/utils/qaAttr';

export interface ContentThumbnailProps {
  /** Thumbnail image URL */
  thumbnailUrl: string | null | undefined;

  /** Alt text for the thumbnail */
  alt?: string;

  /** Custom CSS class */
  className?: string;

  /**
   * Optional QA tag forwarded to the thumbnail root (`data-qa-id`). A
   * `data-qa-status` (`loaded` | `error`) reflecting the existing load state is
   * merged in.
   */
  qa?: QaAttrOptions;
}

export const ContentThumbnail: React.FC<ContentThumbnailProps> = ({
  thumbnailUrl,
  alt = 'Content thumbnail',
  className = '',
  qa,
}) => {
  const { theme } = useTheme();
  const [thumbnailError, setThumbnailError] = useState(false);

  // Reset error when the URL changes so a new src gets a fresh load attempt
  // (e.g., a Processing tile flipping to a real thumbnail post-transcode).
  useEffect(() => {
    setThumbnailError(false);
  }, [thumbnailUrl]);

  const handleThumbnailError = () => {
    setThumbnailError(true);
  };

  const qaWithStatus = qa
    ? { ...qa, status: thumbnailError ? 'error' : 'loaded' }
    : undefined;

  return (
    <div
      {...(qaWithStatus ? qaAttr(qaWithStatus) : {})}
      className={`relative w-full h-full ${className}`}
      style={{ backgroundColor: theme.secondary }}
    >
      {/* Thumbnail Image */}
      {thumbnailUrl && !thumbnailError ? (
        <CachedImage
          src={thumbnailUrl}
          alt={alt}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={handleThumbnailError}
        />
      ) : (
        <div
          className="w-full h-full flex items-center justify-center"
          style={{ backgroundColor: theme.secondary }}
        >
          {/* Display error image in the center */}
          <img
            src="/assets/images/error.png"
            alt="Error loading thumbnail"
            className="w-12 h-12"
          />
        </div>
      )}
    </div>
  );
};
